/** Languages whose stored name is only their code. */
const NAME_BY_CODE: Record<string, string> = { fr: "Français", sw: "Kiswahili", gej: "Gen", kdh: "Tem" };

/** Accent- and case-insensitive key: "Français" and "francais" are one language. */
export const foldLanguageKey = (name: string) =>
  name.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

/**
 * The public spelling of a language as stored in the back office: no country
 * prefix (".tg ewe"), no test entry, first letter capitalised. Empty when the
 * entry must not be shown to the public.
 */
export function cleanLanguageName(code: string, raw: string | undefined, language = "fr"): string {
  const name = (raw ?? "").trim().replace(/^\.[a-z]{2}\s+/i, "").trim();
  if (!name || /test/i.test(name)) return "";
  const known = NAME_BY_CODE[code.toLowerCase()];
  if (known && (name.toLowerCase() === code.toLowerCase() || foldLanguageKey(name) === foldLanguageKey(known))) return known;
  return name.charAt(0).toLocaleUpperCase(language === "en" ? "en-US" : "fr-FR") + name.slice(1);
}

/** Below this many recordings a language is a test or a stray entry, not shown publicly. */
export const MIN_PUBLIC_RECORDINGS = 50;

/**
 * The collected languages as a visitor should read them: clean names, the
 * same language under two codes counted once ("francais" and "français",
 * "mina" and "Mina"), test and stray entries left out.
 */
export function publicLanguages(
  byLanguage: { code: string; name?: string; total: number; accepted: number }[],
  displayNames: Record<string, string> = {},
  language = "fr",
): { code: string; name: string; total: number; accepted: number }[] {
  const merged = new Map<string, { code: string; name: string; total: number; accepted: number; lead: number }>();
  for (const row of byLanguage) {
    const name = cleanLanguageName(row.code, displayNames[row.code.toLowerCase()] || row.name || row.code, language);
    if (!name || row.total <= 0) continue;
    const key = foldLanguageKey(name);
    const entry = merged.get(key);
    if (!entry) {
      merged.set(key, { code: row.code, name, total: row.total, accepted: row.accepted, lead: row.total });
      continue;
    }
    entry.total += row.total;
    entry.accepted += row.accepted;
    if (row.total > entry.lead) Object.assign(entry, { code: row.code, name, lead: row.total });
  }
  return [...merged.values()]
    .filter((row) => row.total >= MIN_PUBLIC_RECORDINGS)
    .map(({ code, name, total, accepted }) => ({ code, name, total, accepted }));
}
